import { Typography } from "@creditsafe/ui-components";
import SearchIcon from "@mui/icons-material/Search";
import type { SxProps } from "@mui/material";
import {
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import type { UIEventHandler } from "react";
import styled from "styled-components";
import type { Profile } from "../../../modules/profiles/types/profile.type";
import type { User } from "../../types/user.type";
import { containsText, getContactName } from "../../../utils";

const FormControlWrapper = styled(FormControl)`
  &.form-control {
    width: 180px;
    margin-left: 8px;
    margin-bottom: 10px;
    height: fit-content;

    .MuiSelect-select {
      padding: 2px 20px 2px 4px;
      border: 1px solid ${(props) => props.theme.greyAdobe1};
      font-size: 12px;

      &:focus-visible {
        outline: none;
      }
    }

    fieldset {
      border: none;
    }

    svg {
      right: 0;
    }
  }

  &.profile-assign-to-user {
    margin-left: 0px;
    margin-bottom: 0px;
    margin-top: 5px;

    .MuiSelect-select {
      border: none;
    }
  }
`;

export const ListSubheaderWrapper = styled(ListSubheader)`
  && {
    padding: 2px 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid ${(props) => props.theme.greyAdobe1};
  }

  &.list-subheader {
    padding: 2px 0 8px;
    margin: 0 4px;

    .search-icon {
      width: 16px;
      margin-right: 4px;
    }

    .search-box {
      width: 144px;

      input {
        padding: 4px;
        border: 1px solid ${(props) => props.theme.greyAdobe1};
        border-radius: 2px;
        font-size: 10px;
      }

      fieldset {
        border: none;
      }
    }
  }
`;

export const MenuItemWrapper = styled(MenuItem)`
  &.menu-item {
    font-size: 12px;
    padding: 4px 8px;
    margin: 0px 4px;
    width: 180px;
    display: block;

    .user-id {
      color: grey;
      display: block;
      font-size: 10px;
    }
  }

  &.unassigned-item {
    border-bottom: 1px solid ${(props) => props.theme.greyAdobe1};
  }

  &.active {
    background-color: ${(props) => props.theme.primaryColor};
  }

  &.menu-item .ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;
  }
`;

const SearchableDropdown: React.FC<{
  list: User[];
  onItemChange: (_selectedId: string | number) => void;
  className?: string;
  selectedId?: string | number;
  isDisabled?: boolean;
  title?: string;
  showId?: boolean;
  infiniteScroll?: boolean;
  handleScrolling?: UIEventHandler<HTMLDivElement>;
  onChangeSearchTerm?: (_value: string) => void;
  searchTerm?: string;
  selectedProfileData?: Profile;
  showSearchBar?: boolean;
  defaultText?: string;
  displayNotFound?: boolean;
  menuItemSx?: SxProps;
}> = ({
  list,
  onItemChange,
  className,
  selectedId: _selectedId,
  isDisabled,
  title = "",
  showId = true,
  infiniteScroll = false,
  handleScrolling,
  onChangeSearchTerm,
  searchTerm,
  selectedProfileData,
  showSearchBar = true,
  defaultText,
  displayNotFound = false,
  menuItemSx,
}) => {
  const validIds = list.map((item) => item.userId.toString());
  const userId =
    _selectedId && validIds.includes(_selectedId.toString()) ? _selectedId : "";

  const [selectedOptionId, setSelectedOptionId] = useState<string | number>(
    userId
  );
  const [searchText, setSearchText] = useState(searchTerm ?? "");

  const displayedOptions = useMemo(
    () =>
      list.filter((option) => containsText(searchText, getContactName(option))),
    [searchText, list]
  );

  const onItemSelect = (
    id: number | string,
    event?: React.MouseEvent<unknown>
  ): void => {
    event?.stopPropagation();
    setSelectedOptionId(id);
    onItemChange(id);
  };

  return (
    <FormControlWrapper fullWidth className={`form-control ${className}`}>
      <Select
        data-private="true"
        value={selectedOptionId}
        variant="standard"
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 240,
            },
            onScroll: infiniteScroll ? handleScrolling : undefined,
          },
          onClick: (e) => e.stopPropagation(),
        }}
        displayEmpty
        aria-label="search-select-label"
        id="search-select"
        onChange={(e) => {
          e.stopPropagation();
          onItemSelect(e.target.value);
        }}
        onClose={() => !infiniteScroll && setSearchText("")}
        renderValue={(id) => {
          if (infiniteScroll && selectedProfileData) {
            return selectedProfileData.name;
          } else {
            const option = list.find((item) => item.userId === id);
            return option ? getContactName(option) : `Select ${title}`;
          }
        }}
        disabled={isDisabled}
      >
        {showSearchBar && (
          <ListSubheaderWrapper className="list-subheader">
            <SearchIcon className="search-icon" />
            <TextField
              size="small"
              className="search-box"
              autoFocus
              placeholder={`Search ${title}s...`}
              id="dropdown-search"
              value={searchTerm}
              onChange={(e) =>
                infiniteScroll
                  ? onChangeSearchTerm && onChangeSearchTerm(e.target.value)
                  : setSearchText(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
              inputProps={{
                "aria-label": "searchUsers",
                "data-private": "true",
              }}
            />
          </ListSubheaderWrapper>
        )}

        {(!showSearchBar || displayNotFound) && (
          <MenuItemWrapper className={"menu-item"} disabled sx={menuItemSx}>
            <Typography
              aria-label="option"
              variant={"body2"}
              data-private="true"
            >
              {defaultText}
            </Typography>
          </MenuItemWrapper>
        )}
        {displayedOptions.map((option) => {
          const contactName = getContactName(option);
          if (contactName) {
            return (
              <MenuItemWrapper
                key={`searchable-dropdown-user-${option.userId}`}
                value={option.userId}
                aria-label={`user ${option.userId}`}
                data-private="true"
                sx={menuItemSx}
                className={
                  "menu-item " +
                  ((selectedOptionId === option.userId + "" ? "active" : "") +
                    (!option.userId || option.userId === "0"
                      ? " unassigned-item"
                      : ""))
                }
              >
                <Typography
                  className="ellipsis"
                  aria-label="option"
                  variant={"body2"}
                  title={contactName}
                  data-private="true"
                >
                  {contactName}
                </Typography>
                {showId && option.userId + "" !== "0" && (
                  <div className="user-id" data-private="true">
                    {option.userId && `ID: ${option.userId}`}
                  </div>
                )}
              </MenuItemWrapper>
            );
          }
          return null;
        })}
      </Select>
    </FormControlWrapper>
  );
};

export default SearchableDropdown;
